import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { CreateEstimationModelRequest } from '@/api/schema/data-contracts';
import { useApi } from '@/context/api';
import { useAuth } from '@/context/auth';

import type { EstimationModelListParams, MutationProps } from '../types';

export const useEstimationModelQueryActions = () => {
  const { auth } = useAuth();
  const {
    estimationModelController: {
      v1EstimationModelsDetail,
      v1EstimationModelsList,
      v1EstimationModelsGroupedList,
      v1EstimationModelsCreate,
      v1EstimationModelsPublishCreate,
      v1EstimationModelsArchiveCreate,
      v1EstimationModelsReactivateCreate,
      v1EstimationModelsAffectedEstimationsCountList,
    },
    estimationModelWithCustomCultureController: {
      v1EstimationModelsDetail: v1EstimationModelsDetailWithCustomCulture,
    },
  } = useApi();

  const queryClient = useQueryClient();

  const useGetEstimationModel = (
    estimationModelId: string | undefined,
    cultureName: string | undefined,
  ) =>
    useQuery({
      enabled: Boolean(auth) && !!estimationModelId,
      queryKey: ['estimation-model-detail', estimationModelId, cultureName],
      queryFn: async () => {
        const getEstimationModelCallback = cultureName
          ? v1EstimationModelsDetailWithCustomCulture
          : v1EstimationModelsDetail;
        const { data } = await getEstimationModelCallback(estimationModelId!, {
          headers: { 'Accept-Language': cultureName },
        });
        return data;
      },
    });

  const useGetEstimationModels = (params?: EstimationModelListParams, predicate?: boolean) =>
    useQuery({
      enabled: Boolean(auth) && predicate !== false,
      queryKey: ['estimation-models', params],
      queryFn: async () => {
        const { data } = await v1EstimationModelsList(params);
        return data;
      },
    });

  const useGetEstimationModelsGrouped = (params?: EstimationModelListParams, predicate?: boolean) =>
    useQuery({
      enabled: Boolean(auth) && predicate !== false,
      queryKey: ['estimation-models-grouped', params],
      queryFn: async () => {
        const { data } = await v1EstimationModelsGroupedList(params);
        return data;
      },
    });

  const useCreateEstimationModel = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['estimation-model-create'],
      mutationFn: async ({
        estimationModel,
      }: {
        estimationModel: CreateEstimationModelRequest;
      }) => {
        const { data } = await v1EstimationModelsCreate(estimationModel);
        return data;
      },
      onSuccess: async (data) => {
        await queryClient.invalidateQueries({
          predicate: (query) =>
            (query.queryKey[0] === 'estimation-model-detail' && query.queryKey[1] === data.id) ||
            query.queryKey[0] === 'estimation-models' ||
            query.queryKey[0] === 'estimation-models-grouped',
        });
      },
    });

  const usePublishEstimationModel = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['estimation-model-publish'],
      mutationFn: async ({ estimationModelId }: { estimationModelId: string }) => {
        const { data } = await v1EstimationModelsPublishCreate(estimationModelId);
        return data;
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'estimation-models' ||
            query.queryKey[0] === 'estimation-models-grouped',
        });
      },
    });

  // EWM-1036: archive/reactivate flip a lineage-wide flag, so every model list and every model
  // detail can be stale afterwards - invalidate the same keys Publish does, plus the detail.
  const invalidateModelQueries = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === 'estimation-model-detail' ||
        query.queryKey[0] === 'estimation-models' ||
        query.queryKey[0] === 'estimation-models-grouped' ||
        query.queryKey[0] === 'estimation-model-affected-estimations-count',
    });
  };

  const useArchiveEstimationModel = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['estimation-model-archive'],
      mutationFn: async ({ estimationModelId }: { estimationModelId: string }) => {
        const { data } = await v1EstimationModelsArchiveCreate(estimationModelId);
        return data;
      },
      onSuccess: invalidateModelQueries,
    });

  const useReactivateEstimationModel = (props?: MutationProps) =>
    useMutation({
      ...props,
      mutationKey: ['estimation-model-reactivate'],
      mutationFn: async ({ estimationModelId }: { estimationModelId: string }) => {
        const { data } = await v1EstimationModelsReactivateCreate(estimationModelId);
        return data;
      },
      onSuccess: invalidateModelQueries,
    });

  /**
   * EWM-1036: impact number for the archive confirmation dialog. `enabled` is the dialog's open
   * state and staleTime is 0, so opening the dialog reads a live count rather than a cached one.
   */
  const useGetEstimationModelAffectedEstimationsCount = (
    estimationModelId: string | undefined,
    enabled: boolean,
  ) =>
    useQuery({
      enabled: Boolean(auth) && !!estimationModelId && enabled,
      queryKey: ['estimation-model-affected-estimations-count', estimationModelId],
      staleTime: 0,
      queryFn: async () => {
        const { data } = await v1EstimationModelsAffectedEstimationsCountList(estimationModelId!);
        return data;
      },
    });

  return {
    useGetEstimationModel,
    useGetEstimationModels,
    useGetEstimationModelsGrouped,
    useCreateEstimationModel,
    usePublishEstimationModel,
    useArchiveEstimationModel,
    useReactivateEstimationModel,
    useGetEstimationModelAffectedEstimationsCount,
  };
};
